import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  ArrowLeft, Plus, ThumbsUp, AlertTriangle, Send, Search,
  MessageSquare, HelpCircle, Bug, Lightbulb, Heart, Lock,
  Pin, Trash2, MoreHorizontal, ChevronLeft, ChevronRight,
  BarChart3, Shield, X, Eye, ImagePlus, Loader2, FileText,
  CheckCircle, Menu, Home, Headset, Pencil, BookMarked
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import ReactMarkdown from "react-markdown";
import "./ForumMarkdown.css";
import NotificationBell from "../components/notifications/NotificationBell";
import AdminMenu from "../components/AdminMenu";
import MainMenu from "../components/MainMenu";
import CoinBalancePill from "../components/CoinBalancePill";

const ICON_MAP = {
  HelpCircle: HelpCircle,
  Bug: Bug,
  MessageSquare: MessageSquare,
  Lightbulb: Lightbulb,
  Heart: Heart,
};

const STATUS_COLORS = {
  Investigating: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  Planned: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  Fixed: "bg-green-500/20 text-green-400 border-green-500/30",
  "Not a Bug": "bg-white/10 text-white/50 border-white/20",
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ============ IMAGE UPLOAD HELPER ============
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============ IMAGE GALLERY (for viewing images in posts/replies) ============
function ImageGallery({ images }) {
  const [lightboxIdx, setLightboxIdx] = useState(null);
  if (!images || images.length === 0) return null;
  return (
    <>
      <div className="flex flex-wrap gap-2 my-2" data-testid="image-gallery">
        {images.map((img, i) => (
          <img
            key={i}
            src={`${API_URL}${img}`}
            alt={`Attachment ${i + 1}`}
            className="h-24 w-24 object-cover rounded-md border border-white/10 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => setLightboxIdx(i)}
            data-testid={`gallery-img-${i}`}
          />
        ))}
      </div>
      {lightboxIdx !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightboxIdx(null)}>
          <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={() => setLightboxIdx(null)}>
            <X className="w-8 h-8" />
          </button>
          <img
            src={`${API_URL}${images[lightboxIdx]}`}
            alt="Full size"
            className="max-w-full max-h-[85vh] rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

// ============ IMAGE PICKER ============
function ImagePicker({ images, setImages, maxImages, label }) {
  const handleAdd = async (e) => {
    const files = Array.from(e.target.files);
    const remaining = maxImages - images.length;
    const toProcess = files.slice(0, remaining);
    const newImages = [];
    for (const f of toProcess) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is too large (max 5MB)`);
        continue;
      }
      const b64 = await fileToBase64(f);
      newImages.push(b64);
    }
    setImages([...images, ...newImages]);
    e.target.value = "";
  };

  return (
    <div data-testid="image-picker">
      <div className="flex flex-wrap gap-2 mb-2">
        {images.map((img, i) => (
          <div key={i} className="relative group">
            <img src={img} alt={`Upload ${i + 1}`} className="h-16 w-16 object-cover rounded-md border border-white/10" />
            <button
              type="button"
              onClick={() => setImages(images.filter((_, j) => j !== i))}
              className="absolute -top-1.5 -right-1.5 bg-red-500/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>
      {images.length < maxImages && (
        <label className="flex items-center gap-1.5 text-white/40 text-xs cursor-pointer hover:text-white/60 transition-colors">
          <ImagePlus className="w-4 h-4" />
          <span>{label || `Add images (${images.length}/${maxImages})`}</span>
          <input type="file" accept="image/*" multiple onChange={handleAdd} className="hidden" />
        </label>
      )}
    </div>
  );
}

// ============ TOPICS LIST VIEW ============
function TopicsListView({ topics, onSelectTopic, user, onCreateTopic, onEditTopic, onDeleteTopic, onOpenSummary, onOpenFaqs, onOpenHashtag }) {
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-3" data-testid="forum-topics-list">
      {/* Top actions — FAQ button is visible to everyone; admin controls flank it */}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={onOpenFaqs}
          variant="outline"
          className="border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200"
          data-testid="open-faqs-btn"
        >
          <BookMarked className="w-4 h-4 mr-2" /> FAQ
        </Button>
        {isAdmin && (
          <>
            <Button
              onClick={onCreateTopic}
              className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
              data-testid="create-topic-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Topic
            </Button>
            <Button
              onClick={onOpenSummary}
              variant="outline"
              className="border-white/10 text-white/60 hover:text-white hover:border-white/20"
              data-testid="admin-summary-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" /> Bug Summary
            </Button>
          </>
        )}
      </div>

      {/* Hashtag cloud sits directly UNDER the action bar so tags don't
          fight the primary CTAs for attention. */}
      <HashtagCloud onOpen={onOpenHashtag} />
      {topics.map((topic) => {
        const IconComp = ICON_MAP[topic.icon] || MessageSquare;
        return (
          <div
            key={topic.topic_id}
            onClick={() => onSelectTopic(topic)}
            className="glass rounded-xl p-5 cursor-pointer hover:bg-white/10 transition-all group border border-white/5 hover:border-divine-gold/20"
            data-testid={`forum-topic-${topic.topic_id}`}
          >
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-lg bg-divine-gold/10 flex items-center justify-center flex-shrink-0">
                <IconComp className="w-5 h-5 text-divine-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-white font-medium text-base">{topic.title}</h3>
                  {topic.is_locked && <Lock className="w-3.5 h-3.5 text-white/30" />}
                  {topic.topic_type === "bug" && (
                    <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-400 px-1.5 py-0">Bug Tracker</Badge>
                  )}
                </div>
                <p className="text-white/50 text-sm mt-0.5">{topic.description}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-white/30">
                  <span>{topic.post_count || 0} posts</span>
                  {topic.latest_post_by && (
                    <span>Latest by {topic.latest_post_by} {timeAgo(topic.latest_post_at)}</span>
                  )}
                </div>
              </div>
              {isAdmin && (
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button
                    variant="ghost" size="icon"
                    onClick={(e) => { e.stopPropagation(); onEditTopic(topic); }}
                    className="h-8 w-8 text-white/40 hover:text-white"
                    data-testid={`edit-topic-${topic.topic_id}`}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost" size="icon"
                    onClick={(e) => { e.stopPropagation(); onDeleteTopic(topic); }}
                    className="h-8 w-8 text-white/40 hover:text-red-400"
                    data-testid={`delete-topic-${topic.topic_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}
      {topics.length === 0 && (
        <div className="text-center py-16 text-white/40">
          <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>No topics yet</p>
        </div>
      )}
    </div>
  );
}

// ============ HASHTAG CLOUD (topics + FAQ headers) ============
// Fetches aggregated tag counts and renders a small pill strip. When
// `faqOnly` is true, backend restricts aggregation to FAQ posts and
// each pill navigates into the FAQ-scoped hashtag drill-down.
function HashtagCloud({ faqOnly = false, onOpen }) {
  const [tags, setTags] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authAxios.get("/forum/hashtags", { params: { faq: faqOnly ? 1 : 0 } });
        if (!cancelled) setTags(res.data.tags || []);
      } catch { /* silent — widget stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [faqOnly]);
  if (!tags.length) return null;
  return (
    <div
      className="glass rounded-xl p-3 border border-white/5 flex flex-wrap items-center gap-1.5"
      data-testid={faqOnly ? "hashtag-cloud-faq" : "hashtag-cloud-all"}
    >
      <span className="text-white/40 text-xs mr-1 uppercase tracking-wider">
        {faqOnly ? "FAQ Hashtags" : "Hashtags"}
      </span>
      {tags.map((t) => (
        <button
          key={t.tag}
          onClick={() => onOpen(t.tag, faqOnly)}
          className="text-xs px-2 py-0.5 rounded-full bg-divine-gold/10 text-divine-gold border border-divine-gold/20 hover:bg-divine-gold/20 hover:border-divine-gold/40 transition-colors"
          data-testid={`hashtag-cloud-pill-${t.tag}`}
          title={`${t.count} post${t.count === 1 ? "" : "s"}`}
        >
          #{t.tag}
          <span className="text-divine-gold/50 ml-1">{t.count}</span>
        </button>
      ))}
    </div>
  );
}

// ============ FAQ LIST VIEW ============
function FaqListView({ posts, loading, onSelectPost, variant = "faq" }) {
  const showIcon = variant === "faq";
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-300" />
      </div>
    );
  }
  if (!posts.length) {
    return (
      <div className="text-center py-16 text-white/40" data-testid="faq-list-empty">
        <BookMarked className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="text-sm">No FAQs yet. Admins can mark useful posts as FAQ to feature them here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="faq-list-view">
      {posts.map((post) => {
        const TopicIcon = ICON_MAP[post.topic?.icon] || MessageSquare;
        return (
          <div
            key={post.post_id}
            onClick={() => onSelectPost(post)}
            className="glass rounded-lg p-4 cursor-pointer hover:bg-white/10 transition-all border border-white/5 hover:border-emerald-500/20"
            data-testid={`faq-post-${post.post_id}`}
          >
            <div className="flex items-start gap-3">
              {showIcon && (
                <div className="w-8 h-8 rounded-md bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <BookMarked className="w-4 h-4 text-emerald-300" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {post.is_pinned && <Pin className="w-3.5 h-3.5 text-divine-gold flex-shrink-0" />}
                  <h4 className="text-white font-medium text-sm truncate">{post.title}</h4>
                  {post.topic && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-white/10 text-white/50">
                      <TopicIcon className="w-2.5 h-2.5 mr-0.5" /> {post.topic.title}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-white/30">
                  <span>{post.author_name}</span>
                  <span>{timeAgo(post.updated_at || post.created_at)}</span>
                  <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {post.like_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {post.reply_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============ EDIT POST MODAL (admin/owner) ============
function EditPostModal({ open, onClose, post, onSave }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [hashtags, setHashtags] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open && post) {
      setTitle(post.title || "");
      setBody(post.body || "");
      setHashtags(Array.isArray(post.hashtags) ? post.hashtags : []);
    }
  }, [open, post]);
  const handleSave = async () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setSaving(true);
    try {
      await onSave(post.post_id, { title: title.trim(), body, hashtags });
      onClose();
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-divine-gold">Edit Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Title"
            className="bg-white/5 border-white/10 text-white"
            data-testid="edit-post-title"
          />
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Body (Markdown supported)"
            className="bg-white/5 border-white/10 text-white min-h-[220px]"
            data-testid="edit-post-body"
          />
          <div>
            <p className="text-white/40 text-xs mb-1">Hashtags</p>
            <HashtagInput value={hashtags} onChange={setHashtags} />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose} className="text-white/60" data-testid="edit-post-cancel">Cancel</Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
              data-testid="edit-post-save"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============ POSTS LIST VIEW ============
function PostsListView({ topic, posts, totalPages, currentPage, onPageChange, onSelectPost, onNewPost, onSearch, user, onToggleFaq }) {
  const [searchQuery, setSearchQuery] = useState("");
  const isBugTopic = topic?.topic_type === "bug";
  const isAdmin = user?.role === "admin";

  const handleSearch = (e) => {
    e.preventDefault();
    onSearch(searchQuery);
  };

  return (
    <div data-testid="forum-posts-list">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md w-full">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              className="pl-9 bg-white/5 border-white/10 text-white placeholder:text-white/30"
              data-testid="forum-search-input"
            />
          </div>
          <Button type="submit" variant="outline" size="sm" className="border-white/10 text-white/60" data-testid="forum-search-btn">
            <Search className="w-4 h-4" />
          </Button>
        </form>
        {!topic?.is_locked && (
          <Button
            onClick={onNewPost}
            className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
            data-testid="new-post-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {posts.map((post) => (
          <div
            key={post.post_id}
            onClick={() => onSelectPost(post)}
            className="glass rounded-lg p-4 cursor-pointer hover:bg-white/10 transition-all border border-white/5 hover:border-white/10"
            data-testid={`forum-post-${post.post_id}`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  {post.is_pinned && <Pin className="w-3.5 h-3.5 text-divine-gold flex-shrink-0" />}
                  <h4 className="text-white font-medium text-sm truncate">{post.title}</h4>
                  {post.is_private && <Lock className="w-3 h-3 text-white/30" />}
                  {/* FAQ pill — admins get a toggle, users see the ON state only */}
                  {isAdmin ? (
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleFaq?.(post); }}
                      className={`text-[10px] px-1.5 py-0.5 rounded-full border transition-colors ${
                        post.is_faq
                          ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40"
                          : "bg-white/5 text-white/40 border-dashed border-white/20 hover:text-white/70 hover:border-white/40"
                      }`}
                      title={post.is_faq ? "Unmark as FAQ" : "Mark as FAQ"}
                      data-testid={`toggle-faq-list-${post.post_id}`}
                    >
                      <BookMarked className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />
                      FAQ
                    </button>
                  ) : (
                    post.is_faq && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-500/10 text-emerald-300 border-emerald-500/30" data-testid={`faq-badge-${post.post_id}`}>
                        <BookMarked className="w-2.5 h-2.5 mr-0.5" /> FAQ
                      </Badge>
                    )
                  )}
                  {isBugTopic && post.status && (
                    <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${STATUS_COLORS[post.status] || ""}`}>
                      {post.status}
                    </Badge>
                  )}
                  {post.helpdesk_ticket_id && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-divine-gold/10 text-divine-gold border-divine-gold/30">
                      <Headset className="w-2.5 h-2.5 mr-0.5" /> Ticket
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-white/30">
                  <span>{post.author_name}</span>
                  <span>{timeAgo(post.created_at)}</span>
                  <span className="flex items-center gap-1"><ThumbsUp className="w-3 h-3" /> {post.like_count || 0}</span>
                  {isBugTopic && <span className="flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {post.metoo_count || 0}</span>}
                  <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> {post.reply_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {posts.length === 0 && (
          <div className="text-center py-12 text-white/40">
            <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No posts yet. Be the first to post!</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button
            variant="ghost" size="sm"
            disabled={currentPage <= 1}
            onClick={() => onPageChange(currentPage - 1)}
            className="text-white/50"
            data-testid="forum-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-white/40 text-sm">Page {currentPage} of {totalPages}</span>
          <Button
            variant="ghost" size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => onPageChange(currentPage + 1)}
            className="text-white/50"
            data-testid="forum-next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ============ POST DETAIL VIEW ============
function PostDetailView({ post, replies, topic, user, onBack, onReact, onReply, onDeletePost, onDeleteReply, onUpdatePost, onCreateTicket, onEditPost, onOpenHashtag }) {
  const [replyBody, setReplyBody] = useState("");
  const [replyImages, setReplyImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isAdmin = user?.role === "admin";
  const isOwner = post?.user_id === user?.user_id;
  const isBugTopic = topic?.topic_type === "bug";
  // Owners retain the edit button until an admin marks the post as FAQ,
  // at which point content is locked to admins only. Comments/replies
  // remain unaffected either way.
  const canEditContent = isAdmin || (isOwner && !post?.is_faq);

  const handleReply = async (e) => {
    e.preventDefault();
    if (!replyBody.trim()) return;
    setSubmitting(true);
    await onReply(replyBody, replyImages);
    setReplyBody("");
    setReplyImages([]);
    setSubmitting(false);
  };

  if (!post) return null;

  return (
    <div data-testid="forum-post-detail">
      {/* Post */}
      <div className="glass rounded-xl p-5 border border-white/5 mb-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              {post.is_pinned && <Pin className="w-4 h-4 text-divine-gold" />}
              <h2 className="text-white font-medium text-lg">{post.title}</h2>
              {post.is_private && (
                <Badge variant="outline" className="text-[10px] border-white/20 text-white/40 px-1.5 py-0">
                  <Lock className="w-2.5 h-2.5 mr-1" /> Private
                </Badge>
              )}
              {post.is_locked && (
                <Badge variant="outline" className="text-[10px] border-orange-500/30 text-orange-400 px-1.5 py-0">Locked</Badge>
              )}
              {/* FAQ badge visible to everyone once ON */}
              {post.is_faq && (
                <Badge variant="outline" className="text-[10px] bg-emerald-500/10 text-emerald-300 border-emerald-500/30 px-1.5 py-0" data-testid="faq-badge-detail">
                  <BookMarked className="w-2.5 h-2.5 mr-0.5" /> FAQ
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-white/40">
              <span>{post.author_name}</span>
              <span>{timeAgo(post.created_at)}</span>
              {post.updated_at !== post.created_at && <span>(edited)</span>}
            </div>
          </div>
          {/* Admin controls */}
          {isAdmin && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="icon"
                onClick={() => onUpdatePost(post.post_id, { is_faq: !post.is_faq })}
                className={`h-7 w-7 ${post.is_faq ? "text-emerald-300" : "text-white/30"}`}
                title={post.is_faq ? "Unmark as FAQ" : "Mark as FAQ"}
                data-testid="toggle-faq-btn"
              >
                <BookMarked className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost" size="icon"
                onClick={() => onEditPost?.(post)}
                className="h-7 w-7 text-white/30 hover:text-divine-gold"
                title="Edit post"
                data-testid="edit-post-admin-btn"
              >
                <Pencil className="w-3.5 h-3.5" />
              </Button>
              {isBugTopic && (
                <Select
                  value={post.status || "none"}
                  onValueChange={(val) => onUpdatePost(post.post_id, { status: val === "none" ? null : val })}
                >
                  <SelectTrigger className="h-7 w-32 text-xs bg-white/5 border-white/10 text-white/60" data-testid="post-status-select">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#1a1a1a] border-white/10">
                    <SelectItem value="none" className="text-white/60 text-xs">No Status</SelectItem>
                    <SelectItem value="Investigating" className="text-yellow-400 text-xs">Investigating</SelectItem>
                    <SelectItem value="Planned" className="text-blue-400 text-xs">Planned</SelectItem>
                    <SelectItem value="Fixed" className="text-green-400 text-xs">Fixed</SelectItem>
                    <SelectItem value="Not a Bug" className="text-white/50 text-xs">Not a Bug</SelectItem>
                  </SelectContent>
                </Select>
              )}
              <Button
                variant="ghost" size="icon"
                onClick={() => onUpdatePost(post.post_id, { is_pinned: !post.is_pinned })}
                className={`h-7 w-7 ${post.is_pinned ? "text-divine-gold" : "text-white/30"}`}
                title={post.is_pinned ? "Unpin" : "Pin"}
                data-testid="toggle-pin-btn"
              >
                <Pin className="w-3.5 h-3.5" />
              </Button>
              <Button
                variant="ghost" size="icon"
                onClick={() => onUpdatePost(post.post_id, { is_locked: !post.is_locked })}
                className={`h-7 w-7 ${post.is_locked ? "text-orange-400" : "text-white/30"}`}
                title={post.is_locked ? "Unlock" : "Lock"}
                data-testid="toggle-lock-btn"
              >
                <Lock className="w-3.5 h-3.5" />
              </Button>
              {post.helpdesk_ticket_id ? (
                <Button
                  variant="ghost" size="icon"
                  onClick={() => window.location.href = `/helpdesk?ticket=${post.helpdesk_ticket_id}`}
                  className="h-7 w-7 text-divine-gold"
                  title="View linked ticket"
                  data-testid="view-ticket-btn"
                >
                  <Headset className="w-3.5 h-3.5" />
                </Button>
              ) : (
                <Button
                  variant="ghost" size="icon"
                  onClick={() => onCreateTicket(post.post_id)}
                  className="h-7 w-7 text-white/30 hover:text-divine-gold"
                  title="Create helpdesk ticket"
                  data-testid="create-ticket-btn"
                >
                  <Headset className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                variant="ghost" size="icon"
                onClick={() => onDeletePost(post.post_id)}
                className="h-7 w-7 text-white/30 hover:text-red-400"
                data-testid="delete-post-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
          {isOwner && !isAdmin && (
            <div className="flex items-center gap-1">
              {canEditContent && (
                <Button
                  variant="ghost" size="icon"
                  onClick={() => onEditPost?.(post)}
                  className="h-7 w-7 text-white/30 hover:text-divine-gold"
                  title="Edit post"
                  data-testid="edit-own-post-btn"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button
                variant="ghost" size="icon"
                onClick={() => onDeletePost(post.post_id)}
                className="h-7 w-7 text-white/30 hover:text-red-400"
                data-testid="delete-own-post-btn"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}
        </div>

        {/* Status badge for bug topics */}
        {isBugTopic && post.status && (
          <Badge variant="outline" className={`text-xs mb-3 ${STATUS_COLORS[post.status] || ""}`}>
            {post.status}
          </Badge>
        )}

        {/* Body - Markdown */}
        <div className="forum-md mb-4">
          <ReactMarkdown>{post.body}</ReactMarkdown>
        </div>

        {/* Post images */}
        <ImageGallery images={post.images} />

        {/* Hashtags — clickable, open /forum/cat/{tag} */}
        {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-1 mb-3" data-testid="post-hashtags">
            {post.hashtags.map((tag) => (
              <button
                key={tag}
                onClick={(e) => { e.stopPropagation(); onOpenHashtag?.(tag); }}
                className="text-xs px-2 py-0.5 rounded-full bg-divine-gold/10 text-divine-gold border border-divine-gold/20 hover:bg-divine-gold/20 hover:border-divine-gold/40 transition-colors"
                data-testid={`post-hashtag-${tag}`}
              >
                #{tag}
              </button>
            ))}
          </div>
        )}

        {/* Poll */}
        {post.poll && <PollWidget poll={post.poll} postId={post.post_id} userId={user?.user_id} />}

        {/* Reactions */}
        <div className="flex items-center gap-2 pt-3 border-t border-white/5">
          <Button
            variant="ghost" size="sm"
            onClick={() => onReact(post.post_id, "post", "like")}
            className={`text-xs gap-1.5 ${post.user_reaction?.includes("like") ? "text-divine-gold bg-divine-gold/10" : "text-white/40"}`}
            data-testid="like-post-btn"
          >
            <ThumbsUp className="w-3.5 h-3.5" /> {post.like_count || 0}
          </Button>
          {isBugTopic && (
            <Button
              variant="ghost" size="sm"
              onClick={() => onReact(post.post_id, "post", "metoo")}
              className={`text-xs gap-1.5 ${post.user_reaction?.includes("metoo") ? "text-red-400 bg-red-500/10" : "text-white/40"}`}
              data-testid="metoo-post-btn"
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Me Too {post.metoo_count || 0}
            </Button>
          )}
          <span className="text-white/20 text-xs ml-auto">{replies.length} {replies.length === 1 ? "reply" : "replies"}</span>
        </div>
      </div>

      {/* Replies */}
      <div className="space-y-2 mb-4">
        {replies.map((reply) => (
          <div
            key={reply.reply_id}
            className={`rounded-lg p-4 border ${
              reply.is_dev_response
                ? "bg-divine-gold/5 border-divine-gold/20"
                : "glass border-white/5"
            }`}
            data-testid={`forum-reply-${reply.reply_id}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 text-xs">
                <span className={reply.is_dev_response ? "text-divine-gold font-medium" : "text-white/60"}>
                  {reply.author_name}
                </span>
                {reply.is_dev_response && (
                  <Badge className="text-[9px] bg-divine-gold/20 text-divine-gold border-divine-gold/30 px-1 py-0">
                    <Shield className="w-2.5 h-2.5 mr-0.5" /> Dev
                  </Badge>
                )}
                <span className="text-white/30">{timeAgo(reply.created_at)}</span>
              </div>
              {(isAdmin || reply.user_id === user?.user_id) && (
                <Button
                  variant="ghost" size="icon"
                  onClick={() => onDeleteReply(reply.reply_id)}
                  className="h-6 w-6 text-white/20 hover:text-red-400"
                  data-testid={`delete-reply-${reply.reply_id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            <div className="forum-md-reply">
              <ReactMarkdown>{reply.body}</ReactMarkdown>
            </div>
            <ImageGallery images={reply.images} />
            <div className="mt-2 pt-2 border-t border-white/5">
              <Button
                variant="ghost" size="sm"
                onClick={() => onReact(reply.reply_id, "reply", "like")}
                className={`text-xs gap-1 ${reply.user_reaction?.includes("like") ? "text-divine-gold bg-divine-gold/10" : "text-white/30"}`}
                data-testid={`like-reply-${reply.reply_id}`}
              >
                <ThumbsUp className="w-3 h-3" /> {reply.like_count || 0}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {/* Reply composer */}
      {(!post.is_locked || isAdmin) && (
        <form onSubmit={handleReply} className="glass rounded-lg p-4 border border-white/5" data-testid="reply-composer">
          <Textarea
            value={replyBody}
            onChange={(e) => setReplyBody(e.target.value)}
            placeholder="Write a reply... (Markdown supported)"
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[80px] mb-3"
            data-testid="reply-textarea"
          />
          <ImagePicker images={replyImages} setImages={setReplyImages} maxImages={3} label={`Attach images (${replyImages.length}/3)`} />
          <div className="flex justify-end mt-2">
            <Button
              type="submit"
              disabled={!replyBody.trim() || submitting}
              className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
              data-testid="submit-reply-btn"
            >
              <Send className="w-4 h-4 mr-2" /> {submitting ? "Sending..." : "Reply"}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}

// ============ POLL WIDGET ============
function PollWidget({ poll, postId, userId }) {
  const [voted, setVoted] = useState(null);
  const [localPoll, setLocalPoll] = useState(poll);

  useEffect(() => {
    if (!poll?.votes) return;
    for (const [opt, voters] of Object.entries(poll.votes)) {
      if (voters.includes(userId)) { setVoted(opt); break; }
    }
  }, [poll, userId]);

  const handleVote = async (option) => {
    try {
      await authAxios.post(`/forum/posts/${postId}/vote`, { option });
      setVoted(option);
      // Optimistic update
      const newPoll = { ...localPoll };
      for (const opt of newPoll.options) {
        newPoll.votes[opt] = (newPoll.votes[opt] || []).filter((v) => v !== userId);
      }
      newPoll.votes[option] = [...(newPoll.votes[option] || []), userId];
      newPoll.total_votes = Object.values(newPoll.votes).reduce((s, v) => s + v.length, 0);
      setLocalPoll(newPoll);
      toast.success("Vote recorded!");
    } catch {
      toast.error("Failed to vote");
    }
  };

  const total = localPoll.total_votes || 1;

  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-4 mb-3" data-testid="poll-widget">
      <p className="text-white/80 text-sm font-medium mb-3">{localPoll.question}</p>
      <div className="space-y-2">
        {localPoll.options.map((opt) => {
          const count = (localPoll.votes[opt] || []).length;
          const pct = Math.round((count / total) * 100);
          const isSelected = voted === opt;
          return (
            <button
              key={opt}
              onClick={() => handleVote(opt)}
              className={`w-full text-left rounded-md p-2.5 text-sm relative overflow-hidden transition-all ${
                isSelected ? "border border-divine-gold/40 bg-divine-gold/10" : "border border-white/10 hover:border-white/20 bg-white/5"
              }`}
              data-testid={`poll-option-${opt}`}
            >
              <div
                className="absolute inset-0 bg-divine-gold/10 transition-all"
                style={{ width: voted ? `${pct}%` : "0%" }}
              />
              <div className="relative flex items-center justify-between">
                <span className={isSelected ? "text-divine-gold" : "text-white/70"}>{opt}</span>
                {voted && <span className="text-white/40 text-xs">{pct}%</span>}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-white/30 text-xs mt-2">{localPoll.total_votes || 0} votes</p>
    </div>
  );
}

// ============ HASHTAG INPUT (chips) ============
// Free-form space/comma separated hashtag input. Normalises client-side
// so the UI matches backend rules (a-z0-9_, max 32 chars, max 10 tags).
function HashtagInput({ value, onChange, disabled }) {
  const [draft, setDraft] = useState("");
  const tags = Array.isArray(value) ? value : [];
  const normalise = (raw) =>
    (raw || "")
      .toLowerCase()
      .replace(/^#/, "")
      .replace(/[^a-z0-9_]/g, "")
      .slice(0, 32);
  const commit = (raw) => {
    const t = normalise(raw);
    if (!t || tags.includes(t) || tags.length >= 10) return;
    onChange([...tags, t]);
  };
  const handleKey = (e) => {
    if ((e.key === "Enter" || e.key === "," || e.key === " ") && draft.trim()) {
      e.preventDefault();
      commit(draft.trim());
      setDraft("");
    } else if (e.key === "Backspace" && !draft && tags.length) {
      onChange(tags.slice(0, -1));
    }
  };
  return (
    <div className="rounded-md bg-white/5 border border-white/10 px-2 py-1.5 flex flex-wrap gap-1.5 items-center min-h-[38px]" data-testid="hashtag-input">
      {tags.map((t) => (
        <span key={t} className="inline-flex items-center gap-1 rounded-full bg-divine-gold/15 text-divine-gold border border-divine-gold/25 text-xs px-2 py-0.5" data-testid={`hashtag-chip-${t}`}>
          #{t}
          {!disabled && (
            <button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} className="text-divine-gold/60 hover:text-divine-gold">
              <X className="w-3 h-3" />
            </button>
          )}
        </span>
      ))}
      {!disabled && tags.length < 10 && (
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKey}
          onBlur={() => { if (draft.trim()) { commit(draft.trim()); setDraft(""); } }}
          placeholder={tags.length ? "" : "Add hashtags (space or enter)"}
          className="flex-1 min-w-[120px] bg-transparent text-sm text-white placeholder:text-white/30 outline-none"
          data-testid="hashtag-input-field"
        />
      )}
    </div>
  );
}

// ============ NEW POST MODAL ============
function NewPostModal({ open, onClose, topicId, onCreated, onViewPost }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [showPoll, setShowPoll] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [postImages, setPostImages] = useState([]);
  const [hashtags, setHashtags] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [checkingDupes, setCheckingDupes] = useState(false);

  // Debounced duplicate check on title change
  useEffect(() => {
    if (!title || title.length < 4) { setDuplicates([]); return; }
    const t = setTimeout(async () => {
      setCheckingDupes(true);
      try {
        const res = await authAxios.get("/forum/suggest-duplicates", { params: { title } });
        setDuplicates(res.data.suggestions || []);
      } catch { /* silent */ }
      setCheckingDupes(false);
    }, 500);
    return () => clearTimeout(t);
  }, [title]);

  const addPollOption = () => {
    if (pollOptions.length < 10) setPollOptions([...pollOptions, ""]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setSubmitting(true);
    try {
      const payload = { topic_id: topicId, title, body, is_private: isPrivate, images: postImages, hashtags };
      if (showPoll && pollQuestion.trim()) {
        const opts = pollOptions.filter((o) => o.trim());
        if (opts.length >= 2) {
          payload.poll = { question: pollQuestion, options: opts };
        }
      }
      const res = await authAxios.post("/forum/posts", payload);
      toast.success("Post created!");
      onCreated(res.data);
      onClose();
      setTitle(""); setBody(""); setIsPrivate(false); setShowPoll(false);
      setPollQuestion(""); setPollOptions(["", ""]); setPostImages([]); setDuplicates([]);
      setHashtags([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create post");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="new-post-desc">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-divine-gold">New Post</DialogTitle>
        </DialogHeader>
        <p id="new-post-desc" className="sr-only">Create a new forum post</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Post title"
            maxLength={200}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            data-testid="new-post-title"
          />

          {/* Duplicate suggestions */}
          {duplicates.length > 0 && (
            <div className="rounded-lg border border-yellow-500/20 bg-yellow-500/5 p-3" data-testid="duplicate-suggestions">
              <p className="text-yellow-400/80 text-xs font-medium mb-2 flex items-center gap-1">
                <FileText className="w-3.5 h-3.5" /> Similar posts found — check before posting:
              </p>
              <div className="space-y-1.5">
                {duplicates.map((d) => (
                  <div key={d.post_id} className="flex items-center gap-2 text-xs text-white/50">
                    <CheckCircle className="w-3 h-3 text-yellow-400/60 flex-shrink-0" />
                    <button type="button" onClick={() => { onClose(); onViewPost(d.post_id); }} className="truncate text-yellow-400/80 hover:text-yellow-300 underline underline-offset-2 text-left">{d.title}</button>
                    <span className="text-white/20 flex-shrink-0">{d.reply_count || 0} replies</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {checkingDupes && <p className="text-white/20 text-xs flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Checking for duplicates...</p>}

          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Write your post... (Markdown supported)"
            maxLength={10000}
            className="bg-white/5 border-white/10 text-white placeholder:text-white/30 min-h-[120px]"
            data-testid="new-post-body"
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Switch checked={isPrivate} onCheckedChange={setIsPrivate} data-testid="new-post-private-toggle" />
              <span className="text-sm text-white/50 flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> Private
              </span>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowPoll(!showPoll)} className="text-white/40 text-xs" data-testid="toggle-poll-btn">
              <BarChart3 className="w-3.5 h-3.5 mr-1" /> {showPoll ? "Remove Poll" : "Add Poll"}
            </Button>
          </div>

          {showPoll && (
            <div className="rounded-lg bg-white/5 border border-white/10 p-3 space-y-2">
              <Input
                value={pollQuestion}
                onChange={(e) => setPollQuestion(e.target.value)}
                placeholder="Poll question"
                maxLength={300}
                className="bg-white/5 border-white/10 text-white text-sm placeholder:text-white/30"
                data-testid="poll-question-input"
              />
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={opt}
                    onChange={(e) => {
                      const next = [...pollOptions];
                      next[i] = e.target.value;
                      setPollOptions(next);
                    }}
                    placeholder={`Option ${i + 1}`}
                    maxLength={100}
                    className="bg-white/5 border-white/10 text-white text-sm placeholder:text-white/30"
                    data-testid={`poll-option-input-${i}`}
                  />
                  {pollOptions.length > 2 && (
                    <Button type="button" variant="ghost" size="icon" onClick={() => setPollOptions(pollOptions.filter((_, j) => j !== i))} className="text-white/30 h-9 w-9">
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
              {pollOptions.length < 10 && (
                <Button type="button" variant="ghost" size="sm" onClick={addPollOption} className="text-white/40 text-xs">
                  <Plus className="w-3 h-3 mr-1" /> Add Option
                </Button>
              )}
            </div>
          )}

          {/* Image picker */}
          <ImagePicker images={postImages} setImages={setPostImages} maxImages={5} label={`Attach images (${postImages.length}/5)`} />

          <div>
            <p className="text-white/40 text-xs mb-1">Hashtags</p>
            <HashtagInput value={hashtags} onChange={setHashtags} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-white/40">Cancel</Button>
            <Button
              type="submit"
              disabled={!title.trim() || !body.trim() || submitting}
              className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30"
              data-testid="submit-new-post-btn"
            >
              {submitting ? "Posting..." : "Post"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ TOPIC MODAL (Create/Edit) ============
function TopicModal({ open, onClose, topic, onSaved }) {
  const [title, setTitle] = useState(topic?.title || "");
  const [description, setDescription] = useState(topic?.description || "");
  const [icon, setIcon] = useState(topic?.icon || "MessageSquare");
  const [topicType, setTopicType] = useState(topic?.topic_type || "general");
  const [sortOrder, setSortOrder] = useState(topic?.sort_order ?? 0);
  const [isLocked, setIsLocked] = useState(topic?.is_locked || false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (topic) {
      setTitle(topic.title || "");
      setDescription(topic.description || "");
      setIcon(topic.icon || "MessageSquare");
      setTopicType(topic.topic_type || "general");
      setSortOrder(topic.sort_order ?? 0);
      setIsLocked(topic.is_locked || false);
    } else {
      setTitle(""); setDescription(""); setIcon("MessageSquare");
      setTopicType("general"); setSortOrder(0); setIsLocked(false);
    }
  }, [topic, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSubmitting(true);
    try {
      const payload = { title, description, icon, topic_type: topicType, sort_order: sortOrder, is_locked: isLocked };
      if (topic?.topic_id) {
        await authAxios.put(`/forum/topics/${topic.topic_id}`, payload);
        toast.success("Topic updated!");
      } else {
        await authAxios.post("/forum/topics", payload);
        toast.success("Topic created!");
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save topic");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#111] border-white/10 text-white max-w-md" aria-describedby="topic-modal-desc">
        <DialogHeader>
          <DialogTitle className="font-cinzel text-divine-gold">
            {topic?.topic_id ? "Edit Topic" : "New Topic"}
          </DialogTitle>
        </DialogHeader>
        <p id="topic-modal-desc" className="sr-only">Create or edit a forum topic</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Topic title" maxLength={200} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="topic-title-input" />
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" maxLength={500} className="bg-white/5 border-white/10 text-white placeholder:text-white/30" data-testid="topic-description-input" />
          <div className="grid grid-cols-2 gap-3">
            <Select value={icon} onValueChange={setIcon}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white/60" data-testid="topic-icon-select">
                <SelectValue placeholder="Icon" />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10">
                {Object.keys(ICON_MAP).map((k) => (
                  <SelectItem key={k} value={k} className="text-white/60">{k}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={topicType} onValueChange={setTopicType}>
              <SelectTrigger className="bg-white/5 border-white/10 text-white/60" data-testid="topic-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1a1a1a] border-white/10">
                <SelectItem value="general" className="text-white/60">General</SelectItem>
                <SelectItem value="bug" className="text-white/60">Bug Tracker</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/50">Sort:</span>
              <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)} className="w-20 bg-white/5 border-white/10 text-white text-sm" data-testid="topic-sort-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={isLocked} onCheckedChange={setIsLocked} data-testid="topic-lock-toggle" />
              <span className="text-sm text-white/50">Locked</span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onClose} className="text-white/40">Cancel</Button>
            <Button type="submit" disabled={!title.trim() || submitting} className="bg-divine-gold/20 text-divine-gold hover:bg-divine-gold/30 border border-divine-gold/30" data-testid="save-topic-btn">
              {submitting ? "Saving..." : "Save"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============ ADMIN SUMMARY VIEW ============
function AdminSummaryView({ onOpenBugsList, onSelectPost }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await authAxios.get("/forum/admin/summary");
        setSummary(res.data);
      } catch {
        toast.error("Failed to load summary");
      }
      setLoading(false);
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-divine-gold" /></div>;
  if (!summary) return null;

  const clickableStats = [
    { key: "open", label: "Open Bugs", value: summary.open_bugs, color: "text-yellow-400", bg: "bg-yellow-500/10", hover: "hover:bg-yellow-500/20 hover:border-yellow-500/30" },
    { key: "fixed", label: "Fixed", value: summary.fixed_bugs, color: "text-green-400", bg: "bg-green-500/10", hover: "hover:bg-green-500/20 hover:border-green-500/30" },
    { key: "not_a_bug", label: "Not a Bug", value: summary.not_a_bug, color: "text-white/50", bg: "bg-white/5", hover: "hover:bg-white/10 hover:border-white/20" },
    { key: null, label: "Total Posts", value: summary.total_posts, color: "text-divine-gold", bg: "bg-divine-gold/10", hover: "" },
  ];

  return (
    <div className="space-y-4" data-testid="admin-summary-view">
      {/* Stats grid — Open/Fixed/Not-a-Bug panels drill into filtered lists */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {clickableStats.map((s) => {
          const isClickable = !!s.key;
          return (
            <button
              key={s.label}
              type="button"
              onClick={() => isClickable && onOpenBugsList?.(s.key, s.label)}
              disabled={!isClickable}
              className={`${s.bg} rounded-lg p-4 border border-white/5 text-left transition-colors ${isClickable ? `cursor-pointer ${s.hover}` : "cursor-default"}`}
              data-testid={`admin-stat-${s.key || "total"}`}
            >
              <p className="text-white/40 text-xs">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            </button>
          );
        })}
      </div>

      {/* Top Me-Too bugs — each row clickable to the post */}
      {summary.top_metoo?.length > 0 && (
        <div className="glass rounded-xl p-4 border border-white/5">
          <h3 className="text-white/70 text-sm font-medium mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" /> Most Reported Bugs
          </h3>
          <div className="space-y-2">
            {summary.top_metoo.map((bug) => (
              <button
                key={bug.post_id}
                type="button"
                onClick={() => onSelectPost?.(bug.post_id)}
                className="w-full flex items-center justify-between text-sm p-2 rounded-md bg-white/5 hover:bg-white/10 transition-colors text-left"
                data-testid={`admin-metoo-${bug.post_id}`}
              >
                <span className="text-white/70 truncate flex-1">{bug.title}</span>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {bug.status && <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[bug.status]}`}>{bug.status}</Badge>}
                  <span className="text-red-400 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {bug.metoo_count}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Needs response — clickable posts + a "See all" pill */}
      {summary.needs_response?.length > 0 && (
        <div className="glass rounded-xl p-4 border border-white/5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white/70 text-sm font-medium flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-divine-gold" /> Awaiting Dev Response
            </h3>
            <button
              type="button"
              onClick={() => onOpenBugsList?.("needs_response", "Awaiting Dev Response")}
              className="text-xs text-divine-gold/70 hover:text-divine-gold"
              data-testid="admin-needs-response-see-all"
            >
              See all →
            </button>
          </div>
          <div className="space-y-2">
            {summary.needs_response.map((p) => (
              <button
                key={p.post_id}
                type="button"
                onClick={() => onSelectPost?.(p.post_id)}
                className="w-full flex items-center justify-between text-sm p-2 rounded-md bg-white/5 hover:bg-white/10 transition-colors text-left"
                data-testid={`admin-needs-response-${p.post_id}`}
              >
                <span className="text-white/70 truncate flex-1">{p.title}</span>
                <span className="text-white/30 text-xs">{timeAgo(p.created_at)}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-white/20 text-xs text-center">
        {summary.total_posts} total posts, {summary.total_replies} total replies
      </p>
    </div>
  );
}

// ============ MAIN FORUM PAGE ============
export default function ForumPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme } = useTheme();
  const isCyber = theme === "agent_eros_1";

  // Views: topics | posts | detail | admin-summary | faqs
  const [view, setView] = useState("topics");
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [posts, setPosts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [postTopic, setPostTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  // FAQ list — cross-topic list of every is_faq post
  const [faqPosts, setFaqPosts] = useState([]);
  const [faqLoading, setFaqLoading] = useState(false);

  // Hashtag list — cross-topic list of every post carrying a given tag
  const [hashtagPosts, setHashtagPosts] = useState([]);
  const [hashtagLoading, setHashtagLoading] = useState(false);
  const [activeHashtag, setActiveHashtag] = useState("");
  const [hashtagFaqMode, setHashtagFaqMode] = useState(false);

  // Admin Bug Summary — filtered bug list state and "return path"
  // remembered so post detail can breadcrumb back to the right parent.
  const [adminBugsPosts, setAdminBugsPosts] = useState([]);
  const [adminBugsLoading, setAdminBugsLoading] = useState(false);
  const [adminBugsFilter, setAdminBugsFilter] = useState(null);
  const [adminBugsLabel, setAdminBugsLabel] = useState("");
  // 'summary' when post was opened directly from AdminSummaryView, 'bugs'
  // when opened from a filtered list. Null when unrelated to admin flow.
  const [adminOriginView, setAdminOriginView] = useState(null);

  // Edit-post modal (admin or owner-of-non-FAQ)
  const [editingPost, setEditingPost] = useState(null);

  // Modals
  const [showNewPost, setShowNewPost] = useState(false);
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);

  // Tracks whether the user landed on this page via a deep-link (e.g. a
  // notification click that hit `/forum?post=xxx`). In that case there is no
  // in-app topic→post navigation chain to walk back through, so the back arrow
  // should return the user to wherever they were before — typically the
  // Dashboard. See handleBack() for the resolution logic.
  const enteredViaDeepLinkRef = useRef(false);
  const entryHistoryLengthRef = useRef(
    typeof window !== "undefined" ? window.history.length : 0
  );

  // Load topics
  const loadTopics = useCallback(async () => {
    try {
      const res = await authAxios.get("/forum/topics");
      setTopics(res.data.topics || []);
    } catch {
      toast.error("Failed to load topics");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTopics(); }, [loadTopics]);

  // Load posts for a topic
  const loadPosts = useCallback(async (topicId, page = 1, search = "") => {
    try {
      const res = await authAxios.get(`/forum/topics/${topicId}/posts`, { params: { page, search } });
      setPosts(res.data.posts || []);
      setTotalPages(res.data.pages || 1);
      setCurrentPage(res.data.page || 1);
    } catch {
      toast.error("Failed to load posts");
    }
  }, []);

  // Load post detail
  const loadPostDetail = useCallback(async (postId) => {
    try {
      const res = await authAxios.get(`/forum/posts/${postId}`);
      setSelectedPost(res.data.post);
      setReplies(res.data.replies || []);
      setPostTopic(res.data.topic);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load post");
    }
  }, []);

  // Deep-link: open post from ?post=xxx query param
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const postId = searchParams.get("post");
    if (postId && !selectedPost) {
      // Mark this session as deep-link entry so the back arrow knows to
      // exit /forum rather than walk a topics→posts chain that doesn't exist.
      enteredViaDeepLinkRef.current = true;
      setView("detail");
      loadPostDetail(postId);
    }
  }, [searchParams, loadPostDetail, selectedPost]);

  // Handlers
  const handleSelectTopic = (topic) => {
    // Any in-app navigation clears the deep-link flag — the user now has a
    // real navigation chain to walk back through.
    enteredViaDeepLinkRef.current = false;
    setSelectedTopic(topic);
    setView("posts");
    loadPosts(topic.topic_id);
  };

  const handleSelectPost = (post) => {
    setView("detail");
    loadPostDetail(post.post_id);
  };

  const handleBack = () => {
    if (view === "detail") {
      // Deep-link entry (e.g. clicked from a notification on /dashboard):
      // there's no topics→posts chain to climb back, and the ?post= param
      // would just re-trigger the deep-link effect causing a back-arrow
      // loop. Step out of /forum entirely using browser history so the user
      // lands back where they came from. Fallback to /dashboard for cases
      // with no real history entry (URL opened in a new tab).
      if (enteredViaDeepLinkRef.current) {
        enteredViaDeepLinkRef.current = false;
        // Strip the query param first so re-entering /forum is clean.
        setSearchParams({}, { replace: true });
        if (window.history.length > entryHistoryLengthRef.current) {
          navigate(-1);
        } else {
          navigate("/dashboard");
        }
        return;
      }
      setView("posts");
      setSelectedPost(null);
      if (selectedTopic) loadPosts(selectedTopic.topic_id, currentPage);
    } else if (view === "posts") {
      setView("topics");
      setSelectedTopic(null);
      loadTopics();
    } else if (view === "admin-summary") {
      setView("topics");
    } else {
      navigate("/dashboard");
    }
  };

  const handleReact = async (targetId, targetType, reactionType) => {
    try {
      await authAxios.post(`/forum/react/${targetType}/${targetId}`, { reaction_type: reactionType });
      if (selectedPost) loadPostDetail(selectedPost.post_id);
    } catch {
      toast.error("Failed to react");
    }
  };

  const handleReply = async (body, images = []) => {
    try {
      await authAxios.post(`/forum/posts/${selectedPost.post_id}/replies`, { body, images });
      toast.success("Reply posted!");
      loadPostDetail(selectedPost.post_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to reply");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await authAxios.delete(`/forum/posts/${postId}`);
      toast.success("Post deleted");
      handleBack();
    } catch {
      toast.error("Failed to delete post");
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm("Delete this reply?")) return;
    try {
      await authAxios.delete(`/forum/replies/${replyId}`);
      toast.success("Reply deleted");
      if (selectedPost) loadPostDetail(selectedPost.post_id);
    } catch {
      toast.error("Failed to delete reply");
    }
  };

  const handleUpdatePost = async (postId, updates) => {
    try {
      await authAxios.put(`/forum/posts/${postId}`, updates);
      toast.success("Post updated");
      // Refresh whichever list is currently in view. The FAQ list caches
      // separately so refresh it whenever we're on the faqs view.
      if (view === "faqs") loadFaqs();
      if (selectedTopic) loadPosts(selectedTopic.topic_id, currentPage);
      loadPostDetail(postId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update post");
    }
  };

  // FAQ list loader — public, includes topic joins for breadcrumbs.
  const loadFaqs = useCallback(async () => {
    setFaqLoading(true);
    try {
      const res = await authAxios.get("/forum/faqs");
      setFaqPosts(res.data.posts || []);
    } catch {
      toast.error("Failed to load FAQs");
    } finally {
      setFaqLoading(false);
    }
  }, []);

  const handleOpenFaqs = () => {
    setSelectedTopic(null);
    setSelectedPost(null);
    setView("faqs");
    loadFaqs();
  };

  // Toggle FAQ from the posts-list row (admins only). Delegates to the
  // shared handler so refresh logic stays centralised.
  const handleToggleFaqRow = (post) => {
    handleUpdatePost(post.post_id, { is_faq: !post.is_faq });
  };

  // Hashtag loader — used both by an in-app click and by the
  // /forum/cat/:hashtag route param on entry. faqMode filters results
  // to FAQ-only when the pill was clicked from the FAQ hashtag cloud.
  const loadHashtag = useCallback(async (tag, faqMode = false) => {
    if (!tag) return;
    setHashtagLoading(true);
    setActiveHashtag(tag);
    setHashtagFaqMode(faqMode);
    try {
      const res = await authAxios.get(`/forum/hashtag/${encodeURIComponent(tag)}`, {
        params: { faq: faqMode ? 1 : 0 },
      });
      setHashtagPosts(res.data.posts || []);
    } catch {
      toast.error("Failed to load hashtag posts");
      setHashtagPosts([]);
    } finally {
      setHashtagLoading(false);
    }
  }, []);

  const handleOpenHashtag = (tag, faqMode = false) => {
    setSelectedTopic(null);
    setSelectedPost(null);
    setAdminOriginView(null);
    setView("hashtag");
    const q = faqMode ? "?faq=1" : "";
    navigate(`/forum/cat/${encodeURIComponent(tag)}${q}`);
    loadHashtag(tag, faqMode);
  };

  // Admin bug summary drill-downs
  const handleOpenBugsList = async (filter, label) => {
    setAdminBugsLoading(true);
    setAdminBugsFilter(filter);
    setAdminBugsLabel(label);
    setSelectedTopic(null);
    setSelectedPost(null);
    setView("admin-bugs");
    try {
      const res = await authAxios.get("/forum/admin/bugs", { params: { filter } });
      setAdminBugsPosts(res.data.posts || []);
    } catch {
      toast.error("Failed to load bug list");
      setAdminBugsPosts([]);
    } finally {
      setAdminBugsLoading(false);
    }
  };

  const handleAdminPickPost = (postId) => {
    // Remember whether we came from the summary landing page or an
    // already-filtered bug list so the breadcrumb rebuilds correctly.
    setAdminOriginView(view === "admin-bugs" ? "bugs" : "summary");
    setSelectedTopic(null);
    setView("detail");
    loadPostDetail(postId);
  };

  // Deep-link: read :hashtag route param on mount / URL change and
  // switch straight into the hashtag view. `?faq=1` search param keeps
  // the FAQ-only filter on refresh.
  const routeParams = useParams();
  useEffect(() => {
    const tag = routeParams.hashtag;
    const wantFaq = searchParams.get("faq") === "1";
    if (tag && (tag !== activeHashtag || wantFaq !== hashtagFaqMode)) {
      enteredViaDeepLinkRef.current = true;
      setView("hashtag");
      loadHashtag(tag, wantFaq);
    }
  }, [routeParams.hashtag, searchParams, activeHashtag, hashtagFaqMode, loadHashtag]);

  const handleDeleteTopic = async (topic) => {
    if (!window.confirm(`Delete "${topic.title}" and all its posts?`)) return;
    try {
      await authAxios.delete(`/forum/topics/${topic.topic_id}`);
      toast.success("Topic deleted");
      loadTopics();
    } catch {
      toast.error("Failed to delete topic");
    }
  };

  const handleCreateTicket = async (postId) => {
    if (!window.confirm("Create a helpdesk ticket from this post?")) return;
    try {
      const res = await authAxios.post(`/forum/posts/${postId}/create-ticket`);
      toast.success("Helpdesk ticket created!");
      loadPostDetail(postId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create ticket");
    }
  };

  const handleSearch = (query) => {
    if (selectedTopic) loadPosts(selectedTopic.topic_id, 1, query);
  };

  const handlePageChange = (page) => {
    if (selectedTopic) loadPosts(selectedTopic.topic_id, page);
  };

  // Hamburger menu state
  const [menuOpen, setMenuOpen] = useState(false);

  // Breadcrumb title
  const pageTitle = view === "detail" && selectedPost
    ? selectedPost.title
    : view === "posts" && selectedTopic
      ? selectedTopic.title
      : view === "admin-summary"
        ? "Admin Summary"
        : view === "faqs"
          ? "FAQ"
          : view === "hashtag"
            ? `#${activeHashtag}`
            : "Forum";

  return (
    <div className="min-h-screen bg-divine-black" data-testid="forum-page">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-divine-black-light border-b border-white/10">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3 min-w-0">
            <Button
              variant="ghost" size="icon"
              onClick={() => navigate("/dashboard")}
              className="text-white/50 hover:text-white flex-shrink-0"
              data-testid="forum-home-btn"
              title="Home"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                alt="Agent Eros"
                className="w-6 h-6 object-contain"
                style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
              />
            </Button>
            <h1 className={`text-lg truncate ${isCyber ? "text-white font-semibold" : "font-cinzel text-white"}`}>
              {pageTitle}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
            <MainMenu exclude="/forum" />
          </div>
        </div>
      </header>

      {/* Breadcrumb — horizontal scroller, styled like /profile submenu */}
      {(() => {
        const goHome = () => {
          setSelectedTopic(null);
          setSelectedPost(null);
          setAdminOriginView(null);
          setActiveHashtag("");
          setHashtagFaqMode(false);
          setView("topics");
          // Also reset the URL so the /forum/cat/:hashtag deep-link
          // effect doesn't immediately push us back into hashtag view.
          navigate("/forum");
          loadTopics();
        };
        const crumbs = [{ label: "Forum", onClick: goHome }];
        if (view === "admin-summary") {
          crumbs.push({ label: "Admin Summary", onClick: null });
        } else if (view === "admin-bugs") {
          crumbs.push({ label: "Admin Summary", onClick: () => { setSelectedPost(null); setView("admin-summary"); } });
          crumbs.push({ label: adminBugsLabel || "Bug List", onClick: null });
        } else if (view === "detail" && adminOriginView) {
          // Post opened from the admin flow — breadcrumb reflects that path.
          crumbs.push({ label: "Admin Summary", onClick: () => { setSelectedPost(null); setAdminOriginView(null); setView("admin-summary"); } });
          if (adminOriginView === "bugs") {
            crumbs.push({ label: adminBugsLabel || "Bug List", onClick: () => { setSelectedPost(null); setView("admin-bugs"); } });
          }
          crumbs.push({ label: selectedPost?.title || "Post", onClick: null });
        } else if (view === "faqs" && !selectedPost) {
          crumbs.push({ label: "FAQ", onClick: null });
        } else if (view === "faqs" && selectedPost) {
          crumbs.push({ label: "FAQ", onClick: () => { setSelectedPost(null); setView("faqs"); } });
          crumbs.push({ label: selectedPost.title, onClick: null });
        } else if (view === "hashtag" && !selectedPost) {
          if (hashtagFaqMode) {
            crumbs.push({ label: "FAQ", onClick: () => { setSelectedPost(null); setView("faqs"); navigate("/forum"); } });
          }
          crumbs.push({ label: `#${activeHashtag}`, onClick: null });
        } else if (view === "hashtag" && selectedPost) {
          if (hashtagFaqMode) {
            crumbs.push({ label: "FAQ", onClick: () => { setSelectedPost(null); setView("faqs"); navigate("/forum"); } });
          }
          crumbs.push({ label: `#${activeHashtag}`, onClick: () => { setSelectedPost(null); setView("hashtag"); } });
          crumbs.push({ label: selectedPost.title, onClick: null });
        } else {
          if (selectedTopic) {
            crumbs.push({
              label: selectedTopic.title,
              onClick: () => { setSelectedPost(null); setView("posts"); },
            });
          }
          if (view === "detail" && selectedPost) {
            crumbs.push({ label: selectedPost.title, onClick: null });
          }
        }
        return (
          <nav
            className="sticky top-14 z-30 flex items-center gap-2 overflow-x-auto whitespace-nowrap bg-divine-charcoal border-b border-divine-gold/20 px-4 py-2 text-xs uppercase tracking-wider"
            data-testid="forum-breadcrumb"
          >
            {crumbs.map((c, i) => (
              <span key={i} className="flex items-center gap-2">
                {i > 0 && <span className="text-white/30">|</span>}
                {c.onClick && i < crumbs.length - 1 ? (
                  <button
                    onClick={c.onClick}
                    className="text-divine-gold hover:text-white transition-colors"
                    data-testid={`forum-breadcrumb-${i}`}
                  >
                    {c.label}
                  </button>
                ) : (
                  <span className="text-white/80">{c.label}</span>
                )}
              </span>
            ))}
          </nav>
        );
      })()}

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            {view === "topics" && (
              <TopicsListView
                topics={topics}
                user={user}
                onSelectTopic={handleSelectTopic}
                onCreateTopic={() => { setEditingTopic(null); setShowTopicModal(true); }}
                onEditTopic={(t) => { setEditingTopic(t); setShowTopicModal(true); }}
                onDeleteTopic={handleDeleteTopic}
                onOpenSummary={() => setView("admin-summary")}
                onOpenFaqs={handleOpenFaqs}
                onOpenHashtag={handleOpenHashtag}
              />
            )}
            {view === "admin-summary" && (
              <AdminSummaryView
                onOpenBugsList={handleOpenBugsList}
                onSelectPost={handleAdminPickPost}
              />
            )}
            {view === "admin-bugs" && (
              <FaqListView
                variant="hashtag"
                posts={adminBugsPosts}
                loading={adminBugsLoading}
                onSelectPost={(p) => handleAdminPickPost(p.post_id)}
              />
            )}
            {view === "faqs" && (
              <div className="space-y-3">
                <HashtagCloud faqOnly onOpen={handleOpenHashtag} />
                <FaqListView
                  posts={faqPosts}
                  loading={faqLoading}
                  onSelectPost={(p) => { setView("detail"); loadPostDetail(p.post_id); setSelectedTopic(null); }}
                />
              </div>
            )}
            {view === "hashtag" && (
              <FaqListView
                variant="hashtag"
                posts={hashtagPosts}
                loading={hashtagLoading}
                onSelectPost={(p) => { setView("detail"); loadPostDetail(p.post_id); setSelectedTopic(null); }}
              />
            )}
            {view === "posts" && selectedTopic && (
              <PostsListView
                topic={selectedTopic}
                posts={posts}
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onSelectPost={handleSelectPost}
                onNewPost={() => setShowNewPost(true)}
                onSearch={handleSearch}
                user={user}
                onToggleFaq={handleToggleFaqRow}
              />
            )}
            {view === "detail" && selectedPost && (
              <PostDetailView
                post={selectedPost}
                replies={replies}
                topic={postTopic}
                user={user}
                onBack={handleBack}
                onReact={handleReact}
                onReply={handleReply}
                onDeletePost={handleDeletePost}
                onDeleteReply={handleDeleteReply}
                onUpdatePost={handleUpdatePost}
                onCreateTicket={handleCreateTicket}
                onEditPost={(p) => setEditingPost(p)}
                onOpenHashtag={handleOpenHashtag}
              />
            )}
          </>
        )}
      </main>

      {/* Modals */}
      {selectedTopic && (
        <NewPostModal
          open={showNewPost}
          onClose={() => setShowNewPost(false)}
          topicId={selectedTopic.topic_id}
          onCreated={() => loadPosts(selectedTopic.topic_id)}
          onViewPost={(postId) => { setView("detail"); loadPostDetail(postId); }}
        />
      )}
      <TopicModal
        open={showTopicModal}
        onClose={() => setShowTopicModal(false)}
        topic={editingTopic}
        onSaved={loadTopics}
      />

      {/* Edit Post modal — reused for admin edits and pre-FAQ owner edits */}
      <EditPostModal
        open={!!editingPost}
        post={editingPost}
        onClose={() => setEditingPost(null)}
        onSave={handleUpdatePost}
      />
    </div>
  );
}
